import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import SearchableSelect from "../components/ui/SearchableSelect";
import { Mail, Send, User, Briefcase, Users, Calendar } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { emailTemplateService } from "../services/emailTemplateService";
import { emailService } from "../services/emailService";
import { leadService } from "../services/leadService";
import { customerService } from "../services/customerService";
import { projectService } from "../services/projectService";
import { useToast } from "../hooks/use-toast";

export default function SendEmail() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [selectedCategory, setSelectedCategory] = useState("customer");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState(null);
  
  const [emailData, setEmailData] = useState({
    template_id: "",
    to_email: "",
    to_name: "",
    subject: "",
    body: "",
    customer_id: null,
    project_id: null,
    lead_id: null,
  });

  // Fetch templates based on selected category
  const { data: templatesData, isLoading: loadingTemplates } = useQuery({
    queryKey: ['email-templates', selectedCategory],
    queryFn: () => emailTemplateService.getAll({ category: selectedCategory, active_only: true })
  });

  // Fetch entities based on category
  const { data: entitiesData, isLoading: loadingEntities } = useQuery({
    queryKey: ['email-entities', selectedCategory],
    queryFn: () => {
      if (selectedCategory === "lead") return leadService.getAll();
      if (selectedCategory === "customer") return customerService.getAll({ page: 1, limit: 100 }); // API max limit is 100
      if (selectedCategory === "project") return projectService.getAll();
      return null;
    }
  });

  const sendEmailMutation = useMutation({
    mutationFn: (data) => emailService.send(data),
    onSuccess: (response) => {
      const warnings = response?.data?.warnings || response?.warnings || [];
      if (warnings.length > 0) {
        toast({ description: `Email sent! Warning: ${warnings.join('; ')}`, duration: 8000 });
      } else {
        toast({ description: "Email sent successfully!" });
      }
      // Reset form
      setSelectedTemplate(null);
      setSelectedEntity(null);
      setEmailData({
        template_id: "",
        to_email: "",
        to_name: "",
        subject: "",
        body: "",
        customer_id: null,
        project_id: null,
        lead_id: null,
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        description: error?.detail || "Failed to send email"
      });
    }
  });

  const templates = templatesData?.data?.templates || [];
  
  // Handle different response structures for different entity types
  const getEntities = () => {
    console.log('SendEmail - entitiesData:', entitiesData);
    console.log('SendEmail - selectedCategory:', selectedCategory);
    
    if (!entitiesData?.data) {
      console.log('SendEmail - No entitiesData.data');
      return [];
    }
    
    console.log('SendEmail - entitiesData.data:', entitiesData.data);
    
    // All entities are in data array directly
    const entities = Array.isArray(entitiesData.data) ? entitiesData.data : [];
    console.log('SendEmail - Entities found:', entities.length);
    return entities;
  };
  
  const entities = getEntities();
  
  console.log('SendEmail - Final entities:', entities);

  // Reset selections when category changes
  useEffect(() => {
    setSelectedTemplate(null);
    setSelectedEntity(null);
    setEmailData({
      template_id: "",
      to_email: "",
      to_name: "",
      subject: "",
      body: "",
      customer_id: null,
      project_id: null,
      lead_id: null,
    });
  }, [selectedCategory]);

  const handleTemplateChange = async (templateId) => {
    try {
      const response = await emailTemplateService.getById(templateId);
      const template = response.data;
      
      setSelectedTemplate(template);
      setEmailData({
        ...emailData,
        template_id: templateId,
        subject: template?.subject || "",
        body: template?.body || ""
      });
    } catch (error) {
      console.error("Failed to fetch template:", error);
    }
  };

  const handleEntityChange = (entityId) => {
    const entity = entities.find(e => (e.id || e._id) === entityId);
    setSelectedEntity(entity);
    
    // Set email and name based on entity type
    setEmailData({
      ...emailData,
      to_email: entity?.email || "",
      to_name: entity?.name || "",
      customer_id: selectedCategory === "customer" ? entityId : null,
      project_id: selectedCategory === "project" ? entityId : null,
      lead_id: selectedCategory === "lead" ? entityId : null,
    });
  };

  const handleSendEmail = async () => {
    // Preview first to check for missing variable data
    try {
      const preview = await emailService.preview(emailData);
      const previewData = preview?.data || preview;
      const warnings = previewData?.warnings || [];

      if (warnings.length > 0) {
        const warningMessages = warnings.map(w => w.message || w).join('\n\n');
        toast({
          variant: "destructive",
          title: "Cannot send — missing variable data",
          description: warningMessages,
          duration: 10000
        });
        return;
      }
    } catch (err) {
      console.error("Preview check failed:", err);
    }

    sendEmailMutation.mutate(emailData);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "lead": return <User className="h-5 w-5" />;
      case "customer": return <Users className="h-5 w-5" />;
      case "project": return <Briefcase className="h-5 w-5" />;
      case "event": return <Calendar className="h-5 w-5" />;
      default: return <Mail className="h-5 w-5" />;
    }
  };

  const getEntityLabel = (category) => {
    switch (category) {
      case "lead": return "Lead";
      case "customer": return "Customer";
      case "project": return "Project";
      case "event": return "Event";
      default: return "Entity";
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Send Email</h1>
          <p className="text-gray-600 mt-1">Send emails using templates to leads, customers, or projects</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Compose Email</CardTitle>
          <CardDescription>Select a template category, choose a recipient, and send</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1: Select Category */}
          <div className="space-y-2">
            <Label>Step 1: Select Template Category</Label>
            <div className="grid grid-cols-4 gap-3">
              {["lead", "customer", "project", "event"].map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  className={selectedCategory === category ? "bg-brand-600 hover:bg-brand-700" : ""}
                  onClick={() => setSelectedCategory(category)}
                >
                  {getCategoryIcon(category)}
                  <span className="ml-2 capitalize">{category}s</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Step 2: Select Entity */}
          <div className="space-y-2">
            <Label htmlFor="entity">Step 2: Select {getEntityLabel(selectedCategory)} *</Label>
            {loadingEntities ? (
              <div className="flex items-center justify-center p-4 border rounded-md">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                <span className="ml-2 text-sm text-gray-500">Loading {getEntityLabel(selectedCategory).toLowerCase()}s...</span>
              </div>
            ) : entities.length === 0 ? (
              <div className="p-4 border rounded-md bg-yellow-50 border-yellow-200">
                <p className="text-sm text-yellow-800">
                  No {getEntityLabel(selectedCategory).toLowerCase()}s found. Please create a {getEntityLabel(selectedCategory).toLowerCase()} first.
                </p>
              </div>
            ) : (
              <SearchableSelect
                items={entities}
                value={selectedEntity?.id || selectedEntity?._id || ""}
                onValueChange={handleEntityChange}
                getItemValue={(item) => item.id || item._id}
                getItemLabel={(item) => `${item.name}${item.email ? ` (${item.email})` : ''}`}
                placeholder={`Choose a ${getEntityLabel(selectedCategory).toLowerCase()}...`}
                searchPlaceholder={`Search ${getEntityLabel(selectedCategory).toLowerCase()}s...`}
                emptyMessage={`No ${getEntityLabel(selectedCategory).toLowerCase()}s found matching search.`}
              />
            )}
          </div>

          {/* Step 3: Select Template */}
          <div className="space-y-2">
            <Label htmlFor="template">Step 3: Select Email Template *</Label>
            {!selectedEntity ? (
              <div className="p-4 border rounded-md bg-gray-50">
                <p className="text-sm text-gray-500">
                  Please select a {getEntityLabel(selectedCategory).toLowerCase()} first
                </p>
              </div>
            ) : loadingTemplates ? (
              <div className="flex items-center justify-center p-4 border rounded-md">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                <span className="ml-2 text-sm text-gray-500">Loading templates...</span>
              </div>
            ) : templates.length === 0 ? (
              <div className="p-4 border rounded-md bg-yellow-50 border-yellow-200">
                <p className="text-sm text-yellow-800">
                  No {selectedCategory} email templates found. Please create a template first in Email Templates.
                </p>
              </div>
            ) : (
              <Select 
                value={emailData.template_id} 
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id || template._id} value={template.id || template._id}>
                      <div className="flex items-center gap-2">
                        {template.name}
                        {template.is_html && <Badge variant="secondary" className="text-xs">HTML</Badge>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Email Details */}
          {selectedTemplate && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="to_email">To Email *</Label>
                  <Input
                    id="to_email"
                    type="email"
                    value={emailData.to_email}
                    onChange={(e) => setEmailData({...emailData, to_email: e.target.value})}
                    placeholder="recipient@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="to_name">To Name *</Label>
                  <Input
                    id="to_name"
                    value={emailData.to_name}
                    onChange={(e) => setEmailData({...emailData, to_name: e.target.value})}
                    placeholder="Recipient Name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject">Subject * (You can override)</Label>
                <Input
                  id="subject"
                  value={emailData.subject}
                  onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                  placeholder="Email subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="body">
                  Body * (You can override)
                  {selectedTemplate.is_html && <Badge variant="secondary" className="ml-2">HTML</Badge>}
                </Label>
                <Textarea
                  id="body"
                  value={emailData.body}
                  onChange={(e) => setEmailData({...emailData, body: e.target.value})}
                  placeholder="Email body"
                  rows={10}
                  className={selectedTemplate.is_html ? "font-mono text-sm" : ""}
                />
              </div>

              {/* Available Variables */}
              {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-sm font-medium text-blue-900">Available Variables</Label>
                  <p className="text-xs text-blue-700 mb-2">
                    These variables will be automatically populated from the selected {getEntityLabel(selectedCategory).toLowerCase()}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.variables.map((variable, index) => (
                      <Badge key={index} variant="outline" className="font-mono text-xs">
                        {`{${variable}}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Send Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  onClick={handleSendEmail}
                  disabled={sendEmailMutation.isPending || !emailData.to_email || !emailData.to_name || !emailData.subject || !emailData.body}
                  className="bg-brand-600 hover:bg-brand-700"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {sendEmailMutation.isPending ? "Sending..." : "Send Email"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
